import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  clampProgressIndex,
  isLearningPlanComplete,
  normalizeActivityProgressStatus,
} from '../../utilities/activitySelection';
import './LearningProgressBar.css';

const getPlannerActivities = (plannerData) => (
  Object.entries(plannerData || {})
    .filter(([key, value]) => (
      typeof value === 'object'
      && value !== null
      && key !== 'goal'
      && key !== 'number_activities'
    ))
    .map(([, activity]) => activity)
);

/**
 * Plan-level progress computed purely from the planner data: the server
 * mirrors each activity's aggregate progress (best attempt) into the planner
 * doc, and PlannerContext merges live event-bus updates into it — no
 * per-render fetching here.
 */
const calculateProgressData = (plannerData) => {
  const totalActivities = plannerData?.number_activities || 0;
  const activities = getPlannerActivities(plannerData);

  let progressTotal = 0;
  let completedCount = 0;

  activities.forEach(activity => {
    // Best attempt per activity — the plan average never regresses on a redo
    const progressIndex = clampProgressIndex(activity.progress?.progressIndex ?? 0);
    progressTotal += progressIndex;
    if (normalizeActivityProgressStatus(activity.progress?.status, progressIndex) === 'completed') {
      completedCount++;
    }
  });

  const percentage = totalActivities > 0 ? Math.round(progressTotal / totalActivities) : 0;

  return {
    totalActivities,
    completedCount,
    percentage,
  };
};

function LearningProgressBar({ plannerData, showLabel = true, className = "" }) {
  const { t } = useTranslation();
  const progressData = useMemo(() => (
    plannerData && plannerData.number_activities ? calculateProgressData(plannerData) : null
  ), [plannerData]);

  if (!progressData) {
    return null;
  }

  const { totalActivities, completedCount, percentage } = progressData;

  return (
    <div className={`learning-progress-bar ${className}`}>
      {showLabel && (
        <div className="progress-header">
          <span className="progress-label">{t('learning_progress')}</span>
          <span className="progress-stats">
            {completedCount}/{totalActivities} {t('activities')} • {percentage}%
          </span>
        </div>
      )}
      <div className="progress-bar-container">
        <div className="progress-bar-background">
          <div
            className="progress-bar-fill"
            style={{ width: `${percentage}%` }}
          >
            <div className="progress-bar-shine"></div>
          </div>
        </div>
        {!showLabel && (
          <div className="progress-percentage-inline">
            {percentage}%
          </div>
        )}
      </div>
      {showLabel && (
        <div className="progress-footer">
          <small className="progress-description">
            {/* Same helper the completion card uses, so the two can't disagree */}
            {isLearningPlanComplete(plannerData)
              ? t('learning_plan_complete_hint', 'Plan complete! Set a new goal to keep going')
              : percentage === 0
              ? t('start_your_first_activity')
              : percentage === 100
              ? t('all_activities_started')
              : t('keep_practicing_activities')
            }
          </small>
        </div>
      )}
    </div>
  );
}

export default LearningProgressBar;
